import type { AppProps, AppContext } from "next/app";
import App from "next/app";
import { setAppContext } from "@utils/Cookies";

import type { PageProps, PageWithLayout, RequestMeta } from "@types";
import type { IntlError } from "use-intl";

import { useEffect } from "react";
import dynamic from "next/dynamic";
import { IntlProvider, IntlErrorCode } from "use-intl";
import { SWRConfig } from "swr";
import {
  getLayout as getDefaultLayout,
  noLayout,
  embedLayout,
} from "@layouts/default-layout";
import { fetcher, serialize } from "@utils/swr";
import AppProvider from "context/app";
import { ManagedUIContext } from "@context/ui-managed-context";
import CartProvider from "@context/cart";
import PlayerProvider from "@context/player";
import { COOKIE_ACCESS_TOKEN_KEY } from "@utils/constants";
import Cookie from "@utils/Cookie";
import { initGA, logPageView } from "lib/ga";
import { resolveAndMapFallbackDataAsync } from "@utils/api";
import { StaticLayoutDT } from "types/api/layout";
import LayoutProvider from "@context/layout";
import { getSkippedLayoutPrefetchRequests } from "constants/layout-consts";
import "../styles/index.css";
import ResizeHandler from "legacy/elements/resize-handler";
import LocaleHandler from "legacy/elements/locale-handler";
import MTM from "legacy/elements/mtm";
const ModalCreator = dynamic(
  () => import("legacy/elements/modal/modal-creator"),
  { ssr: false }
);
import Yandex from "legacy/elements/YandexScript";
import GTMScript from "legacy/elements/GTM";
import Clarity from "legacy/elements/clarity";
import homeApi from "@features/home/helpers/homeApi";
import SiteHeadTags from "legacy/elements/SiteHeadTags";
import { defaultTimeZone } from "@utils/translates";

function onError(error: IntlError) {
  console.error(error);
}

function getMessageFallback({
  namespace,
  key,
  error,
}: {
  namespace?: string | undefined;
  key: string;
  error: IntlError;
}) {
  const path = [namespace, key].filter((part) => part != null).join(".");

  if (error.code === IntlErrorCode.MISSING_MESSAGE) {
    return `${path} is not yet translated`;
  } else {
    return `Dear developer, please fix this message: ${path}`;
  }
}

function MyApp(props: AppProps<PageProps> & { err: any }) {
  const {
    Component,
    router,
    err,
    pageProps: { messages, now, ...restProps },
  } = props;

  const {
    getLayout = getDefaultLayout,
    translationFile,
    isLiteEmbed,
  } = Component as PageWithLayout<PageProps>;

  const isInternationalScriptsDisabled =
    process.env.NEXT_PUBLIC_IS_INTERNATIONAL_SCRIPTS_ACTIVE === "false";

  useEffect(() => {
    if (isInternationalScriptsDisabled || isLiteEmbed) return;

    initGA(process.env.NEXT_PUBLIC_GA4_ID || "");

    logPageView();
    const handleRouteChange = () => {
      logPageView();
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events, isInternationalScriptsDisabled, isLiteEmbed]);

  useEffect(() => {
    const handleErrorObserver = (e: ErrorEvent) => {
      if (
        e.message ===
          "ResizeObserver loop completed with undelivered notifications." ||
        e.message === "ResizeObserver loop limit exceeded"
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };
    window.addEventListener("error", handleErrorObserver);

    const mainHostName = window.location.hostname.includes("faradars.org");
    function cookieHasDomain(cookieName: string) {
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        // Check if the cookie has a domain
        if (cookie.startsWith(`${cookieName}=`)) {
          const cookieParts = cookie.split("=");
          if (cookieParts.length > 1) {
            const cookieValue = cookieParts[1];
            // Check if the cookie value contains a domain
            if (cookieValue.includes("domain=")) {
              return true;
            }
          }
        }
      }
      return !mainHostName;
    }

    const hasDomain = cookieHasDomain(COOKIE_ACCESS_TOKEN_KEY);
    const cookie = new Cookie();

    if (!hasDomain) {
      cookie.remove(COOKIE_ACCESS_TOKEN_KEY, false);
    }

    () => {
      window.removeEventListener("error", handleErrorObserver);
    };
  }, []);

  const fallback = props.pageProps.fallback || {};
  const appFallback = props.pageProps.appFallback || {};
  useEffect(() => {
    document.body.classList?.remove("loading");
    if (
      typeof window !== "undefined" &&
      "scrollRestoration" in window.history
    ) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  const locale = router.locale || router.defaultLocale || "fa";
  const layoutRenderer = isLiteEmbed ? embedLayout : getLayout || noLayout;

  return (
    <IntlProvider
      formats={{
        dateTime: {
          short: {
            day: "numeric",
            month: "short",
            year: "numeric",
          },
        },
      }}
      locale={locale}
      timeZone={defaultTimeZone}
      messages={{
        ...require(`../locales/${locale}/common.json`),
        ...(translationFile
          ? require(`../locales/${locale}/${translationFile}.json`)
          : {}),
        ...messages,
      }}
      now={new Date(now || new Date())}
      getMessageFallback={getMessageFallback}
      onError={onError}
    >
      <AppProvider>
        <SWRConfig
          value={{
            fetcher,
            use: [serialize],
            fallback: { ...fallback, ...appFallback },
          }}
        >
          <LayoutProvider lite={isLiteEmbed}>
            <ManagedUIContext>
              <PlayerProvider>
                <CartProvider>
                  <ModalCreator />
                  {!isLiteEmbed && (
                    <>
                      <SiteHeadTags />
                      <ResizeHandler />
                      <LocaleHandler router={router} />
                      <MTM />
                      <Yandex />
                      <GTMScript />
                      {!isInternationalScriptsDisabled && <Clarity />}
                    </>
                  )}
                  {layoutRenderer(Component, {
                    ...restProps,
                    err,
                    props,
                  })}
                </CartProvider>
              </PlayerProvider>
            </ManagedUIContext>
          </LayoutProvider>
        </SWRConfig>
      </AppProvider>
    </IntlProvider>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);

  setAppContext(appContext);

  const pathname = appContext?.router?.pathname;
  const isEmbedPage =
    typeof pathname === "string" && pathname.startsWith("/embed");

  if (isEmbedPage) return appProps;

  const skippedRequests = getSkippedLayoutPrefetchRequests(pathname || "");
  const skipStaticLayout = skippedRequests.includes("staticLayout");
  const skipFestivalBanner = skippedRequests.includes("festivalBanner");
  const skipFooter = skippedRequests.includes("footer");

  const staticLayoutRequest = skipStaticLayout
    ? null
    : homeApi.getStaticLayout();
  const layoutRequests: RequestMeta<any, never, never>[] = [
    ...(staticLayoutRequest ? [staticLayoutRequest] : []),
    ...(skipFestivalBanner ? [] : [homeApi.getFestivalBanner()]),
    ...(skipFooter ? [] : [homeApi.getFooter()]),
  ];

  if (!layoutRequests.length) return appProps;

  let err: any = null;

  try {
    const cachedData = await resolveAndMapFallbackDataAsync(...layoutRequests);
    const layoutStaticData: StaticLayoutDT | undefined = staticLayoutRequest
      ? cachedData[staticLayoutRequest.key()]
      : undefined;

    return {
      ...appProps,
      pageProps: {
        ...appProps.pageProps,
        appFallback: cachedData || {},
        static_layout: layoutStaticData,
        err,
      },
    };
  } catch (error) {
    err = error;
  }

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      err,
    },
  };
};
export default MyApp;
